import * as pdfjsLib from 'pdfjs-dist';
import { classifyFont } from './font-analysis.js';
/**
 * Extract pdf.js TextItems with resolved font info, normalized to top-origin coordinates.
 *
 * Font resolution: `page.commonObjs.get()` returns an internal ref ("g_d0_f1") in many
 * environments (especially Node), NOT the PostScript name. The reliable source is
 * `textContent.styles[fontName].fontFamily` — pdf.js populates this with the resolved
 * CSS font-family string (e.g., "Times-Bold", "Helvetica-Oblique", or fallback families).
 *
 * Coordinate normalization: pdf.js uses bottom-left origin; we flip y so it grows
 * downward (top-origin) to match human reading order.
 */
export async function extractStyledItems(buffer) {
    const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
    const items = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1 });
        const pageHeight = viewport.height;
        // CRITICAL: run operator list FIRST to force font resolution before getTextContent.
        // Without this, commonObjs.get() throws "isn't resolved yet" and styles dict only
        // contains generic CSS family names (serif/sans-serif). After getOperatorList(),
        // commonObjs returns the real PostScript name (e.g. "Arial-BoldMT", "FGFIPA+BookAntiqua-Bold").
        await page.getOperatorList();
        const textContent = await page.getTextContent();
        // styles dict (CSS family) is the fallback for fonts where commonObjs still fails.
        const styles = textContent.styles ?? {};
        for (const raw of textContent.items) {
            if (!isTextItem(raw))
                continue;
            // Preserve pure-whitespace items — they carry real word-boundary signals
            // that emphasis-merge needs to recover spaces between adjacent text runs.
            if (!raw.str)
                continue;
            const transform = raw.transform;
            if (!Array.isArray(transform) || transform.length < 6)
                continue;
            const a = numberAt(transform, 0); // x scale ~ font size
            const d = numberAt(transform, 3);
            const e = numberAt(transform, 4); // x
            const fY = numberAt(transform, 5); // y (bottom-origin)
            const height = raw.height && raw.height > 0 ? raw.height : Math.max(Math.abs(d), Math.abs(a));
            const width = raw.width ?? 0;
            // Resolve real PostScript name first (most reliable for bold/italic detection).
            let resolvedFontName = '';
            try {
                const fontObj = page.commonObjs.get(raw.fontName);
                resolvedFontName = fontObj?.name ?? '';
            }
            catch {
                // Fall through.
            }
            // Fall back to styles dict CSS family if PostScript name is unavailable.
            if (!resolvedFontName) {
                const styleEntry = raw.fontName ? styles[raw.fontName] : undefined;
                resolvedFontName = styleEntry?.fontFamily ?? raw.fontName ?? '';
            }
            const { bold, italic } = classifyFont(resolvedFontName);
            // Note: we deliberately don't infer italic from transform skew. Real-world PDFs
            // (especially PowerPoint exports) carry small skew noise on every glyph that
            // would trigger false positives. Font name is the reliable signal — italic fonts
            // virtually always have "Italic"/"Oblique" in their PostScript name.
            // Flip y: convert bottom-origin (PDF) to top-origin (reading order).
            const yTopOrigin = pageHeight - fY - height;
            items.push({
                str: raw.str,
                x: e,
                y: yTopOrigin,
                xEnd: e + width,
                height,
                fontName: resolvedFontName,
                bold,
                italic,
                page: pageNum,
            });
        }
    }
    return { items, pageCount: pdf.numPages };
}
function isTextItem(item) {
    return 'str' in item;
}
function numberAt(arr, idx) {
    const v = arr[idx];
    return typeof v === 'number' ? v : 0;
}
//# sourceMappingURL=extract.js.map